import React, { useState, useEffect, useContext } from "react";
import "./Dashboard.css";
import DropDown from "../../Components/DropDown/DropDown";
import ViewProductsModal from "../../Components/ViewProductsModal/ViewProductsModal";
import AuthContext from "../../context/Services/Auth";
import GlobalContext from "../../context/GlobalContext";

import { IoDocumentTextOutline } from "react-icons/io5";
import { RiArrowUpSLine, RiArrowDownSLine } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import { FiCalendar } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

import {
  statuses_overview,
  search_by_status,
  get_monthly_summary,
  get_sales_chart_data,
  get_commercial_sales_data,
  get_client_sales_data,
  get_period_deliveries,
} from "../../context/Services/Dashboard";
import { get_all_staff } from "../../context/Services/Staff";
import { get_all_stock } from "../../context/Services/Stock";
import { get_all_clients } from "../../context/Services/Clients";

const filter_time = [
  {
    _id: "all",
    value: "All time",
  },
  {
    _id: "today",
    value: "today",
  },
  {
    _id: "week",
    value: "This week",
  },
  {
    _id: "month",
    value: "This month",
  },
  {
    _id: "year",
    value: "This year",
  },
];

const Dashboard = () => {
  const { t } = useTranslation();
  const { userData } = useContext(AuthContext);
  const { store } = useContext(GlobalContext);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showFullTable, setShowFullTable] = useState(false);
  const [monthlySummaryData, setMonthlySummaryData] = useState([]);

  const [showProductsModal, setShowProductsModal] = useState({
    status: false,
    data: [],
  });

  const [statusesData, setStatusesData] = useState({
    values: [],
    total: 0,
    total_amount: 0,
  });

  const [selectedFilters, setSelectedFilters] = useState({
    category: null,
    time_status: null,
    time_money: null,
  });

  // Chart data states
  const [salesChartData, setSalesChartData] = useState([]);
  const [commercialChartData, setCommercialChartData] = useState([]);
  const [salesViewType, setSalesViewType] = useState("monthly");
  const [commercialViewType, setCommercialViewType] = useState("monthly");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedCommercial, setSelectedCommercial] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [commercialUsers, setCommercialUsers] = useState([]);
  
  // Client chart states
  const [clientChartData, setClientChartData] = useState([]);
  const [clientViewType, setClientViewType] = useState("monthly");
  const [selectedClient, setSelectedClient] = useState("");
  const [clientsList, setClientsList] = useState([]);

  const [chartMonth, setChartMonth] = useState(new Date().getMonth() + 1);

  // Chart details modal state
  const [chartDetailsModal, setChartDetailsModal] = useState({
    status: false,
    data: [],
    title: "",
  });

  const get_statuses_overview = (filter) => {
    statuses_overview(filter)
      .then((res) => {
        setStatusesData(res?.data);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handle_get_monthly_summary = () => {
    get_monthly_summary(selectedYear)
      .then((res) => {
        setMonthlySummaryData(res?.data || []);
      })
      .catch((e) => console.log(e));
  };

  // Fetch stock items for product filter
  const handle_get_stock = async () => {
    try {
      const res = await get_all_stock({ query: "" });
      const stockData = res?.data?.stock || res?.stock || [];
      setStockItems(stockData);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch commercial users for filter
  const handle_get_commercials = async () => {
    try {
      const res = await get_all_staff();
      const commercials = (res?.data || []).filter(u => u.role === "commercial");
      setCommercialUsers(commercials);
      if (commercials.length > 0 && !selectedCommercial) {
        setSelectedCommercial(commercials[0]._id);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch clients for filter
  const handle_get_clients = async () => {
    try {
      const res = await get_all_clients({});
      const clients = res?.data || [];
      setClientsList(clients);
      if (clients.length > 0 && !selectedClient) {
        setSelectedClient(clients[0]._id);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch sales chart data
  const handle_get_sales_chart = async () => {
    try {
      const month = salesViewType === "daily" ? chartMonth : null;
      const res = await get_sales_chart_data(salesViewType, selectedYear, month, selectedProduct || null);
      setSalesChartData(res?.data || []);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch commercial sales chart data
  const handle_get_commercial_chart = async () => {
    if (!selectedCommercial) return;
    try {
      const month = commercialViewType === "daily" ? chartMonth : null;
      const res = await get_commercial_sales_data(commercialViewType, selectedYear, month, selectedCommercial);
      setCommercialChartData(res?.data || []);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch client sales chart data
  const handle_get_client_chart = async () => {
    if (!selectedClient) return;
    try {
      const month = clientViewType === "daily" ? chartMonth : null;
      const res = await get_client_sales_data(clientViewType, selectedYear, month, selectedClient);
      setClientChartData(res?.data || []);
    } catch (e) {
      console.log(e);
    }
  };

  // Handle click on sales chart bar
  const handleSalesChartClick = async (data) => {
    if (!data || !data.activePayload) return;
    const payload = data.activePayload[0]?.payload;
    if (!payload) return;

    try {
      const monthIndex = salesViewType === "monthly"
        ? ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].indexOf(payload.label) + 1
        : chartMonth;
      const day = salesViewType === "daily" ? parseInt(payload.label) : null;

      const res = await get_period_deliveries(
        salesViewType,
        selectedYear,
        monthIndex,
        day,
        null,
        selectedProduct || null
      );

      setChartDetailsModal({
        status: true,
        data: res?.data || [],
        title: `${t("Détails")} - ${payload.label} ${salesViewType === "monthly" ? selectedYear : ""}`,
      });
    } catch (e) {
      console.log(e);
    }
  };

  // Handle click on commercial chart bar
  const handleCommercialChartClick = async (data) => {
    if (!data || !data.activePayload) return;
    const payload = data.activePayload[0]?.payload;
    if (!payload) return;

    try {
      const monthIndex = commercialViewType === "monthly"
        ? ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].indexOf(payload.label) + 1
        : chartMonth;
      const day = commercialViewType === "daily" ? parseInt(payload.label) : null;

      const res = await get_period_deliveries(
        commercialViewType,
        selectedYear,
        monthIndex,
        day,
        selectedCommercial,
        null
      );

      const commercialName = commercialUsers.find(u => u._id === selectedCommercial)?.full_name || "";
      setChartDetailsModal({
        status: true,
        data: res?.data || [],
        title: `${commercialName} - ${payload.label} ${commercialViewType === "monthly" ? selectedYear : ""}`,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const show_products = (status) => {
    const get_period =
      filter_time.find((elem) => elem?.value === selectedFilters?.time_status)
        ?._id || null;

    search_by_status(status, get_period)
      .then((res) => {
        if (res?.data?.length > 0) {
          setShowProductsModal({
            status: true,
            data: res?.data,
          });
        }
      })
      .catch((e) => console.log(e));
  };

  // Calculate totals from monthly data
  const calculateTotals = () => {
    return monthlySummaryData.reduce(
      (acc, month) => {
        acc.totalShipped += month.totalShipped || 0;
        acc.totalReturned += month.totalReturned || 0;
        acc.totalPending += month.totalPending || 0;
        acc.totalItems += month.totalItems || 0;
        acc.totalSellPrice += month.totalSellPrice || 0;
        return acc;
      },
      { totalShipped: 0, totalReturned: 0, totalPending: 0, totalItems: 0, totalSellPrice: 0 }
    );
  };

  const totals = calculateTotals();

  useEffect(() => {
    get_statuses_overview(filter_time[3]?._id);
    setSelectedFilters((prev) => ({
      ...prev,
      time_status: filter_time[3]?.value,
    }));
    handle_get_stock();
    handle_get_commercials();
    handle_get_clients();
  }, [store]);

  useEffect(() => {
    handle_get_monthly_summary();
    handle_get_sales_chart();
  }, [selectedYear, store, salesViewType, selectedProduct, chartMonth]);

  useEffect(() => {
    handle_get_commercial_chart();
  }, [selectedYear, selectedCommercial, commercialViewType, chartMonth, store]);

  useEffect(() => {
    handle_get_client_chart();
  }, [selectedYear, selectedClient, clientViewType, chartMonth, store]);

  // Generate year options (current year and 4 previous years)
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 5; i++) {
    yearOptions.push(currentYear - i);
  }

  return (
    <div className="dashboard_main flex flex-col gap">
      <ViewProductsModal
        show={showProductsModal.status}
        setShow={() => setShowProductsModal({ status: false, data: [] })}
        data={showProductsModal.data}
      />

      {/* Transactions Financières - Status Summary */}
      <div className="flex flex-col total_contacts_sections gap shadow">
        <div className="flex items-center">
          <b>{t("Transactions Financières")}</b>

          <div className="relative to-right">
            <DropDown
              button={
                <>
                  <FiCalendar style={{ color: "#7983ff" }} />
                  <b>
                    {selectedFilters?.time_status
                      ? t(selectedFilters?.time_status)
                      : t("All time")}
                  </b>
                  <IoMdArrowDropdown />
                </>
              }
            >
              {filter_time.map((elem, i) => (
                <div
                  key={i}
                  className="drop_item"
                  onClick={() => {
                    get_statuses_overview(elem?._id);
                    setSelectedFilters((prev) => ({
                      ...prev,
                      time_status: elem?.value,
                    }));
                  }}
                >
                  {t(elem.value)}
                </div>
              ))}
            </DropDown>
          </div>
        </div>

        <div className="flex items-center w-full summary_kpis_container">
          <div className="flex items-center gap">
            <div className="document_icon_container flex justify-center items-center">
              <IoDocumentTextOutline className="doc_icon" />
            </div>
            <div className="flex flex-col">
              <h6 className="total less_black">
                {statusesData?.total} {t("Articles")}
              </h6>
              <p className="total_money">
                {statusesData?.total_amount?.toLocaleString("en-US")} {t("MAD")}
              </p>
            </div>
          </div>

          <div className="flex gap to-right summary_kpis">
            {statusesData?.values?.length > 0 &&
              statusesData.values.map((elem, i) => (
                <div className="flex flex-col kpi" key={i}>
                  <h6 className="total less_black">{elem?.value}</h6>
                  <p className="total_money">
                    {elem?.total_amount?.toLocaleString("en-US")} {t("MAD")}
                  </p>
                  <span
                    className="nugget_status pointer"
                    style={{
                      backgroundColor: elem?.status?.bg_color,
                      color: elem?.status?.text_color,
                    }}
                    onClick={() => show_products(elem?.status?._id)}
                  >
                    {elem?.status?.value?.toLocaleString("en-US")}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Sales/Returns KPI Charts - Admin Only */}
      {userData?.role === "admin" && (
        <>
        <div className="charts_grid limited_screen">
          {/* Left Chart: Total Sales/Returns */}
          <div className="chart_card shadow">
            <div className="chart_section_header flex items-center">
              <b>{t("Ventes / Retours")}</b>
              <div className="relative to-right flex gap">
                {/* View Type Filter */}
                <DropDown
                  button={
                    <>
                      <FiCalendar style={{ color: "#7983ff" }} />
                      <b>{salesViewType === "daily" ? t("Journalier") : t("Mensuel")}</b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  <div className="drop_item" onClick={() => setSalesViewType("monthly")}>
                    {t("Mensuel")}
                  </div>
                  <div className="drop_item" onClick={() => setSalesViewType("daily")}>
                    {t("Journalier")}
                  </div>
                </DropDown>

                {/* Month Filter (only for daily view) */}
                {salesViewType === "daily" && (
                  <DropDown
                    button={
                      <>
                        <b>{["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"][chartMonth - 1]}</b>
                        <IoMdArrowDropdown />
                      </>
                    }
                  >
                    {["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].map((m, i) => (
                      <div key={i} className="drop_item" onClick={() => setChartMonth(i + 1)}>
                        {m}
                      </div>
                    ))}
                  </DropDown>
                )}

                {/* Product Filter */}
                <DropDown
                  button={
                    <>
                      <b>
                        {selectedProduct
                          ? stockItems.find((s) => s._id === selectedProduct)?.product_name || t("Tous les produits")
                          : t("Tous les produits")}
                      </b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  <div className="drop_item" onClick={() => setSelectedProduct("")}>
                    {t("Tous les produits")}
                  </div>
                  {stockItems.map((item) => (
                    <div
                      key={item._id}
                      className="drop_item"
                      onClick={() => setSelectedProduct(item._id)}
                    >
                      {item.product_name}
                    </div>
                  ))}
                </DropDown>
              </div>
            </div>
            <div className="chart_container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barGap={2} barCategoryGap="20%" onClick={handleSalesChartClick} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}K` : v} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="custom_chart_tooltip shadow">
                          <div className="tooltip_header">
                            <b>{label}</b>
                          </div>
                          <table className="tooltip_table">
                            <tbody>
                              <tr>
                                <td className="tooltip_label">{t("Vendu")}</td>
                                <td className="tooltip_value sold">{payload[0]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Retourné")}</td>
                                <td className="tooltip_value returned">{payload[1]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Montant Vendu")}</td>
                                <td className="tooltip_value">{(payload[0]?.payload?.soldAmount || 0).toLocaleString()} MAD</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="sold" name={t("Vendu")} fill="#8B5CF6" radius={[6, 6, 6, 6]} />
                  <Bar dataKey="returned" name={t("Retourné")} fill="#3B82F6" radius={[6, 6, 6, 6]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right Chart: Commercial Sales */}
          <div className="chart_card shadow">
            <div className="chart_section_header flex items-center">
              <b>{t("Ventes Commercial")}</b>
              <div className="relative to-right flex gap">
                {/* View Type Filter */}
                <DropDown
                  button={
                    <>
                      <FiCalendar style={{ color: "#7983ff" }} />
                      <b>{commercialViewType === "daily" ? t("Journalier") : t("Mensuel")}</b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  <div className="drop_item" onClick={() => setCommercialViewType("monthly")}>
                    {t("Mensuel")}
                  </div>
                  <div className="drop_item" onClick={() => setCommercialViewType("daily")}>
                    {t("Journalier")}
                  </div>
                </DropDown>

                {/* Month Filter (only for daily view) */}
                {commercialViewType === "daily" && (
                  <DropDown
                    button={
                      <>
                        <b>{["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"][chartMonth - 1]}</b>
                        <IoMdArrowDropdown />
                      </>
                    }
                  >
                    {["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].map((m, i) => (
                      <div key={i} className="drop_item" onClick={() => setChartMonth(i + 1)}>
                        {m}
                      </div>
                    ))}
                  </DropDown>
                )}

                {/* Commercial Filter */}
                <DropDown
                  button={
                    <>
                      <b>
                        {selectedCommercial
                          ? commercialUsers.find((u) => u._id === selectedCommercial)?.full_name || t("Sélectionner")
                          : t("Sélectionner")}
                      </b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  {commercialUsers.map((user) => (
                    <div
                      key={user._id}
                      className="drop_item"
                      onClick={() => setSelectedCommercial(user._id)}
                    >
                      {user.full_name}
                    </div>
                  ))}
                </DropDown>
              </div>
            </div>
            <div className="chart_container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={commercialChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barGap={2} barCategoryGap="20%" onClick={handleCommercialChartClick} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}K` : v} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="custom_chart_tooltip shadow">
                          <div className="tooltip_header">
                            <b>{label}</b>
                          </div>
                          <table className="tooltip_table">
                            <tbody>
                              <tr>
                                <td className="tooltip_label">{t("Vendu")}</td>
                                <td className="tooltip_value sold">{payload[0]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Retourné")}</td>
                                <td className="tooltip_value returned">{payload[1]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Montant Vendu")}</td>
                                <td className="tooltip_value">{(payload[0]?.payload?.soldAmount || 0).toLocaleString()} MAD</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="sold" name={t("Vendu")} fill="#8B5CF6" radius={[6, 6, 6, 6]} />
                  <Bar dataKey="returned" name={t("Retourné")} fill="#3B82F6" radius={[6, 6, 6, 6]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Third Chart: Client Sales - Full Width */}
        <div className="charts_grid limited_screen" style={{ marginTop: '20px', gridTemplateColumns: '1fr' }}>
          <div className="chart_card shadow">
            <div className="chart_section_header flex items-center">
              <b>{t("Ventes Client")}</b>
              <div className="relative to-right flex gap">
                {/* View Type Filter */}
                <DropDown
                  button={
                    <>
                      <FiCalendar style={{ color: "#7983ff" }} />
                      <b>{clientViewType === "daily" ? t("Journalier") : t("Mensuel")}</b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  <div className="drop_item" onClick={() => setClientViewType("monthly")}>
                    {t("Mensuel")}
                  </div>
                  <div className="drop_item" onClick={() => setClientViewType("daily")}>
                    {t("Journalier")}
                  </div>
                </DropDown>

                {/* Month Filter (only for daily view) */}
                {clientViewType === "daily" && (
                  <DropDown
                    button={
                      <>
                        <b>{["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"][chartMonth - 1]}</b>
                        <IoMdArrowDropdown />
                      </>
                    }
                  >
                    {["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"].map((m, i) => (
                      <div key={i} className="drop_item" onClick={() => setChartMonth(i + 1)}>
                        {m}
                      </div>
                    ))}
                  </DropDown>
                )}

                {/* Client Filter */}
                <DropDown
                  button={
                    <>
                      <b>
                        {selectedClient
                          ? clientsList.find((c) => c._id === selectedClient)?.name || t("Sélectionner")
                          : t("Sélectionner")}
                      </b>
                      <IoMdArrowDropdown />
                    </>
                  }
                >
                  {clientsList.map((client) => (
                    <div
                      key={client._id}
                      className="drop_item"
                      onClick={() => setSelectedClient(client._id)}
                    >
                      {client.name}
                    </div>
                  ))}
                </DropDown>
              </div>
            </div>
            <div className="chart_container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={clientChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barGap={2} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}K` : v} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="custom_chart_tooltip shadow">
                          <div className="tooltip_header">
                            <b>{label}</b>
                          </div>
                          <table className="tooltip_table">
                            <tbody>
                              <tr>
                                <td className="tooltip_label">{t("Vendu")}</td>
                                <td className="tooltip_value sold">{payload[0]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Retourné")}</td>
                                <td className="tooltip_value returned">{payload[1]?.value || 0}</td>
                              </tr>
                              <tr>
                                <td className="tooltip_label">{t("Montant Vendu")}</td>
                                <td className="tooltip_value">{(payload[0]?.payload?.soldAmount || 0).toLocaleString()} MAD</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="sold" name={t("Vendu")} fill="#10B981" radius={[6, 6, 6, 6]} />
                  <Bar dataKey="returned" name={t("Retourné")} fill="#F43F5E" radius={[6, 6, 6, 6]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Récapitulatif Mensuel - Monthly Summary Table */}
      {userData?.role === "admin" && (
        <div className="clients_overview latest_clients_overview shadow limited_screen">
          <div className="chart_section_header flex items-center">
            <b>{t("Récapitulatif Mensuel")}</b>
            <div className="relative to-right flex gap">
              <DropDown
                button={
                  <>
                    <FiCalendar style={{ color: "#7983ff" }} />
                    <b>{selectedYear}</b>
                    <IoMdArrowDropdown />
                  </>
                }
              >
                {yearOptions.map((year) => (
                  <div
                    key={year}
                    className="drop_item"
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </div>
                ))}
              </DropDown>
              <div
                className="flex gap items-center filter_button"
                onClick={() => setShowFullTable(!showFullTable)}
              >
                {showFullTable ? (
                  <RiArrowUpSLine
                    style={{ color: "#7983ff", fontSize: "1.2rem" }}
                  />
                ) : (
                  <RiArrowDownSLine
                    style={{ color: "#7983ff", fontSize: "1.2rem" }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap summary_clients_section">
            <div className="flex stock_head gap ml_15 mt_-10">
              <div className="flex gap total_div">
                <p>{t("Total Livré")}</p>
                <b>{totals.totalShipped}</b>
              </div>
              <div className="flex gap total_div">
                <p>{t("Total Retourné")}</p>
                <b>{totals.totalReturned}</b>
              </div>
              <div className="flex gap total_div">
                <p>{t("En Cours")}</p>
                <b>{totals.totalPending}</b>
              </div>
              <div className="flex gap total_div">
                <p>{t("Total Montant")}</p>
                <b>{totals.totalSellPrice?.toLocaleString("en-US")} {t("MAD")}</b>
              </div>
            </div>

            <div
              className="table_section"
              style={{ maxHeight: showFullTable ? "none" : "400px", overflow: "auto" }}
            >
              <table cellPadding="0" cellSpacing="0">
                <thead>
                  <tr style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#fff" }}>
                    <th className="table-keys cell2">{t("Mois")}</th>
                    <th className="table-keys cell2">{t("Livré")}</th>
                    <th className="table-keys cell2">{t("Retourné")}</th>
                    <th className="table-keys cell2">{t("En Cours")}</th>
                    <th className="table-keys cell2">{t("Total Articles")}</th>
                    <th className="table-keys cell2">{t("Montant Total")}</th>
                  </tr>
                </thead>
                <tbody>

                  {monthlySummaryData?.length > 0 ? (
                    monthlySummaryData.map((row, i) => (
                      <tr className="data-cells" key={i}>
                        <td className="value2 cell2" style={{ fontSize: "0.85rem" }}>
                          {row?.monthName}
                        </td>
                        <td className="value2 cell2">
                          {row?.totalShipped || 0}
                        </td>
                        <td className="value2 cell2">
                          {row?.totalReturned || 0}
                        </td>
                        <td className="value2 cell2">
                          {row?.totalPending || 0}
                        </td>
                        <td className="value2 cell2">{row?.totalItems || 0}</td>
                        <td className="value2 cell2">
                          {(row?.totalSellPrice || 0).toLocaleString("en-US")} {t("MAD")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("No data available")}
                      </td>
                    </tr>
                  )}

                  {/* Totals Row */}
                  {monthlySummaryData?.length > 0 && (
                    <tr className="data-cells" style={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                      <td className="value2 cell2">
                        <b>{t("Total")}</b>
                      </td>
                      <td className="value2 cell2">
                        <b>{totals.totalShipped}</b>
                      </td>
                      <td className="value2 cell2">
                        <b>{totals.totalReturned}</b>
                      </td>
                      <td className="value2 cell2">
                        <b>{totals.totalPending}</b>
                      </td>
                      <td className="value2 cell2">
                        <b>{totals.totalItems}</b>
                      </td>
                      <td className="value2 cell2">
                        <b>{totals.totalSellPrice?.toLocaleString("en-US")} {t("MAD")}</b>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Chart Details Modal */}
      <ViewProductsModal
        show={chartDetailsModal.status}
        setShow={() => setChartDetailsModal({ status: false, data: [], title: "" })}
        data={chartDetailsModal.data}
      />
    </div>
  );
};

export default Dashboard;
